import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, Euro, ArrowLeft, FileText, Shield, CheckCircle, Flame, Swords, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

// Programme détaillé du Stage Cascade
const programmeJours = [
  {
    jour: 1,
    titre: "Introduction & bases de la cascade physique",
    duree: "7h",
    objectifs: [
      "Découvrir l'univers de la cascade professionnelle",
      "Identifier les règles de sécurité et conditions d'entraînement",
      "Apprendre à chuter et réagir à un impact"
    ],
    contenus: [
      "Accueil, briefing, présentation de la formation et de l'équipe",
      "Échauffement collectif et détection du niveau des stagiaires",
      "Exercices de chute à sa hauteur et gestion de l'impact",
      "Réaction à un tir ou un coup simulé"
    ],
    materiel: "Tapis, protections diverses, cartons, salle équipée"
  },
  {
    jour: 2,
    titre: "Combat de base : donner et recevoir des coups",
    duree: "7h",
    objectifs: [
      "Intégrer les techniques fondamentales de frappe en cascade",
      "Apprendre à réagir de manière crédible et contrôlée"
    ],
    contenus: [
      "Techniques de coups de base : gifles, poings (direct, crochet, uppercut)",
      "Coudes et manchettes",
      "Exercices en binôme pour coordination et précision",
      "Premiers enchaînements simples"
    ],
    materiel: "Protections adaptées, zone sécurisée, tapis"
  },
  {
    jour: 3,
    titre: "Combats chorégraphiés & objets en cascade",
    duree: "7h",
    objectifs: [
      "Enchaîner les coups dans une logique chorégraphiée",
      "Introduire l'utilisation d'objets dans une scène d'action"
    ],
    contenus: [
      "Coups avancés : coups de pied (direct, latéral, au sol), genoux",
      "Combinaisons de coups portés",
      "Création de petits enchaînements scénarisés",
      "Utilisation d'objets factices : matraques, armes, couteaux"
    ],
    materiel: "Protections, matériels de frappe, objets factices, caméras"
  },
  {
    jour: 4,
    titre: "Cascades techniques : chutes en hauteur & feu",
    duree: "7h",
    objectifs: [
      "Réaliser des chutes en hauteur dans le respect des normes de sécurité",
      "Préparer et exécuter une cascade feu partielle ou totale"
    ],
    contenus: [
      "Sauts progressifs de 2 à 8 mètres : installation, sécurisation, réception",
      "Briefing sécurité et démonstrations techniques",
      "Préparation et réalisation d'une cascade feu",
      "Utilisation du stunt gel, protection, extinction"
    ],
    materiel: "Tapis, cartons, airbag, échafaudage, tenues ignifugées, extincteurs"
  },
  {
    jour: 5,
    titre: "Mise en pratique & évaluation finale",
    duree: "7h",
    objectifs: [
      "Mobiliser l'ensemble des compétences dans une situation filmée",
      "Être capable d'analyser et d'ajuster sa pratique"
    ],
    contenus: [
      "Création collective d'un scénario d'action court",
      "Répétitions et tournage final",
      "Direction d'acteurs",
      "Visionnage des scènes tournées, débriefing collectif"
    ],
    materiel: "Caméras, protections, accessoires, zone de tournage"
  }
];

const TrainingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const fetchTraining = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trainings/${id}`);
      setTraining(response.data);
    } catch (error) {
      console.error('Error fetching training:', error);
      // Fallback data
      setTraining({
        id: id,
        title: 'Stage Cascade - Formation Complète',
        description: 'Formation aux fondamentaux de la cascade physique et de l\'action cinéma. Acquérir les bases techniques, physiques et artistiques.',
        price: 1800,
        date: '2026-03-10',
        duration: '5 jours (35h)',
        max_participants: 12,
        current_participants: 0,
        category: 'Formation Complète',
        image_url: 'https://images.unsplash.com/photo-1510177087648-3ae3fa149848?w=800',
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTraining();
  }, [fetchTraining]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const regResponse = await axios.post(`${API}/registrations`, {
        training_id: id,
        ...formData
      });

      const origin = window.location.origin;
      const checkoutResponse = await axios.post(
        `${API}/payments/checkout/training`,
        null,
        {
          params: {
            registration_id: regResponse.data.id,
            origin_url: origin
          }
        }
      );

      if (checkoutResponse.data.url) {
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <p className="text-gray-400">Formation non trouvée</p>
      </div>
    );
  }

  const isFull = training.current_participants >= training.max_participants;

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Back button */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/trainings')}
          className="text-gray-400 hover:text-[#FACC15]"
          data-testid="back-button"
        >
          <ArrowLeft size={20} className="mr-2" />
          Retour aux formations
        </Button>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-24">
        {/* Header */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative aspect-[21/9] overflow-hidden mb-8"
          >
            <img
              src={training.image_url || 'https://images.unsplash.com/photo-1510177087648-3ae3fa149848?w=1200'}
              alt={training.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider">
                {training.category}
              </span>
              <h1 
                className="font-['Teko'] text-5xl md:text-7xl text-white mt-2"
                data-testid="training-title"
              >
                {training.title}
              </h1>
            </div>
          </motion.div>

          {/* Quick Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#09090b] border border-white/10 p-4">
              <Calendar size={20} className="text-[#FACC15] mb-2" />
              <p className="font-['Barlow'] text-gray-400 text-sm">Date</p>
              <p className="font-['Barlow'] text-white">{formatDate(training.date)}</p>
            </div>
            <div className="bg-[#09090b] border border-white/10 p-4">
              <Clock size={20} className="text-[#FACC15] mb-2" />
              <p className="font-['Barlow'] text-gray-400 text-sm">Durée</p>
              <p className="font-['Barlow'] text-white">{training.duration}</p>
            </div>
            <div className="bg-[#09090b] border border-white/10 p-4">
              <Users size={20} className="text-[#FACC15] mb-2" />
              <p className="font-['Barlow'] text-gray-400 text-sm">Places</p>
              <p className="font-['Barlow'] text-white">
                {training.current_participants}/{training.max_participants}
              </p>
            </div>
            <div className="bg-[#09090b] border border-white/10 p-4">
              <Euro size={20} className="text-[#FACC15] mb-2" />
              <p className="font-['Barlow'] text-gray-400 text-sm">Prix</p>
              <p className="font-['Teko'] text-3xl text-[#FACC15]">{training.price}€</p>
            </div>
          </div>

          {/* AFDAS Badge */}
          <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 p-4 mb-8">
            <div className="flex items-center gap-4">
              <span className="font-['Teko'] text-3xl text-[#FACC15]">AFDAS</span>
              <div>
                <p className="font-['Barlow'] text-white font-medium">Financement possible</p>
                <p className="font-['Barlow'] text-gray-400 text-sm">Cette formation est éligible au financement AFDAS pour les intermittents du spectacle</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left - Programme */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="programme" className="w-full">
              <TabsList className="bg-[#09090b] border border-white/10 p-1 w-full grid grid-cols-3">
                <TabsTrigger value="programme" className="font-['Barlow'] uppercase text-sm data-[state=active]:bg-[#FACC15] data-[state=active]:text-black">
                  Programme
                </TabsTrigger>
                <TabsTrigger value="formateurs" className="font-['Barlow'] uppercase text-sm data-[state=active]:bg-[#FACC15] data-[state=active]:text-black">
                  Formateurs
                </TabsTrigger>
                <TabsTrigger value="infos" className="font-['Barlow'] uppercase text-sm data-[state=active]:bg-[#FACC15] data-[state=active]:text-black">
                  Infos pratiques
                </TabsTrigger>
              </TabsList>

              <TabsContent value="programme" className="mt-6">
                <p className="font-['Barlow'] text-gray-300 text-lg leading-relaxed mb-8">
                  {training.description}
                </p>

                <div className="space-y-6">
                  {programmeJours.map((jour, index) => (
                    <motion.div
                      key={jour.jour}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-[#09090b] border border-white/10 overflow-hidden"
                    >
                      <div className="bg-[#18181b] p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="font-['Teko'] text-4xl text-[#FACC15]">J{jour.jour}</span>
                          <div>
                            <h3 className="font-['Teko'] text-xl text-white uppercase">{jour.titre}</h3>
                            <p className="font-['Barlow'] text-gray-400 text-sm">{jour.duree}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="mb-4">
                          <h4 className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-2">Objectifs</h4>
                          <ul className="space-y-1">
                            {jour.objectifs.map((obj) => (
                              <li key={obj} className="font-['Barlow'] text-gray-300 text-sm flex items-start gap-2">
                                <CheckCircle size={14} className="text-[#FACC15] mt-1 flex-shrink-0" />
                                {obj}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="mb-4">
                          <h4 className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-2">Contenus</h4>
                          <ul className="space-y-1">
                            {jour.contenus.map((content) => (
                              <li key={content} className="font-['Barlow'] text-gray-400 text-sm">• {content}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="font-['Barlow'] text-gray-500 text-xs">
                            <span className="text-[#FACC15]">Matériel :</span> {jour.materiel}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="formateurs" className="mt-6">
                <div className="space-y-6">
                  {[
                    {
                      name: 'André FERRER',
                      role: 'Comédien cascadeur, Régleur cascade',
                      bio: 'Plus de 20 ans d\'expérience dans les cascades pour le cinéma et la télévision. Spécialiste combat et coordination d\'action.'
                    },
                    {
                      name: 'Éric TATIN',
                      role: 'Régleur cascade, Spécialisé câblage',
                      bio: 'Expert en cascades techniques, chutes en hauteur et effets de vol. Formateur certifié en sécurité cascade.'
                    },
                    {
                      name: 'Alexandra CHALLET',
                      role: 'Scénariste, Réalisatrice d\'action & documentaire',
                      bio: '2ème équipe action, actrice. Spécialiste en chorégraphie de combat et direction artistique des séquences d\'action.'
                    }
                  ].map((formateur) => (
                    <div key={formateur.name} className="bg-[#09090b] border border-white/10 p-6 flex gap-6">
                      <div className="w-20 h-20 bg-[#FACC15]/10 flex-shrink-0 flex items-center justify-center">
                        <User size={32} className="text-[#FACC15]" />
                      </div>
                      <div>
                        <h3 className="font-['Teko'] text-2xl text-white uppercase">{formateur.name}</h3>
                        <p className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-2">{formateur.role}</p>
                        <p className="font-['Barlow'] text-gray-400 text-sm">{formateur.bio}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="infos" className="mt-6">
                <div className="space-y-6">
                  <div className="bg-[#09090b] border border-white/10 p-6">
                    <h3 className="font-['Teko'] text-xl text-white uppercase mb-4 flex items-center gap-2">
                      <Shield className="text-[#FACC15]" /> Sécurité
                    </h3>
                    <ul className="space-y-2 font-['Barlow'] text-gray-400 text-sm">
                      <li>• Présence de personnel médical sur site</li>
                      <li>• Équipements de protection fournis</li>
                      <li>• Zones de pratique sécurisées</li>
                      <li>• Encadrement par des professionnels certifiés</li>
                    </ul>
                  </div>

                  <div className="bg-[#09090b] border border-white/10 p-6">
                    <h3 className="font-['Teko'] text-xl text-white uppercase mb-4">Public visé</h3>
                    <ul className="space-y-2 font-['Barlow'] text-gray-400 text-sm">
                      <li>• Comédiens débutants souhaitant s'initier aux cascades</li>
                      <li>• Aspirants cascadeurs</li>
                      <li>• Professionnels du spectacle</li>
                      <li>• Cascadeurs confirmés en perfectionnement</li>
                    </ul>
                  </div>

                  <div className="bg-[#09090b] border border-white/10 p-6">
                    <h3 className="font-['Teko'] text-xl text-white uppercase mb-4">Évaluation</h3>
                    <ul className="space-y-2 font-['Barlow'] text-gray-400 text-sm">
                      <li>• Évaluation continue tout au long de la formation</li>
                      <li>• Mise en situation finale : scène d'action filmée</li>
                      <li>• Critères : sécurité, crédibilité, précision technique</li>
                      <li>• Fiche de suivi personnalisé</li>
                      <li>• Attestation de fin de formation</li>
                    </ul>
                  </div>

                  <div className="bg-[#09090b] border border-white/10 p-6">
                    <h3 className="font-['Teko'] text-xl text-white uppercase mb-4">Organisme de formation</h3>
                    <p className="font-['Barlow'] text-white mb-2">EUREKA & STUNT DIRECTOR TRAINING</p>
                    <p className="font-['Barlow'] text-gray-400 text-sm">
                      Formation professionnelle encadrée par une équipe de cascadeurs professionnels 
                      et formateurs spécialisés en action cinéma.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right - Registration Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-[#09090b] border border-white/10 p-8">
                <h2 className="font-['Teko'] text-3xl text-white mb-2 uppercase">
                  Inscription
                </h2>
                <p className="font-['Barlow'] text-gray-400 text-sm mb-6">
                  Réservez votre place pour cette formation
                </p>

                {isFull ? (
                  <div className="text-center py-8">
                    <p className="font-['Barlow'] text-[#EF4444] text-lg mb-4">
                      Cette formation est complète
                    </p>
                    <p className="font-['Barlow'] text-gray-400">
                      Contactez-nous pour être informé des prochaines sessions.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[#FACC15] text-xs uppercase tracking-wider mb-1 block">
                          Prénom
                        </Label>
                        <Input
                          type="text"
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          className="bg-black border-white/20 focus:border-[#FACC15] text-white text-sm"
                          data-testid="input-firstname"
                        />
                      </div>
                      <div>
                        <Label className="text-[#FACC15] text-xs uppercase tracking-wider mb-1 block">
                          Nom
                        </Label>
                        <Input
                          type="text"
                          required
                          value={formData.last_name}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          className="bg-black border-white/20 focus:border-[#FACC15] text-white text-sm"
                          data-testid="input-lastname"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#FACC15] text-xs uppercase tracking-wider mb-1 block">
                        Email
                      </Label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="bg-black border-white/20 focus:border-[#FACC15] text-white text-sm"
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <Label className="text-[#FACC15] text-xs uppercase tracking-wider mb-1 block">
                        Téléphone
                      </Label>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-black border-white/20 focus:border-[#FACC15] text-white text-sm"
                        data-testid="input-phone"
                      />
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-['Barlow'] text-gray-400">Total</span>
                        <span className="font-['Teko'] text-4xl text-[#FACC15]">{training.price}€</span>
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider py-6 hover:bg-[#EAB308] transition-colors"
                        data-testid="submit-registration"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <div className="spinner" />
                            Traitement...
                          </span>
                        ) : (
                          'Payer et s\'inscrire'
                        )}
                      </Button>

                      <p className="font-['Barlow'] text-gray-500 text-xs text-center mt-4">
                        Paiement sécurisé par Stripe
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingDetail;
